import React from 'react';
import {
  ClaimAdjustmentViewDTO,
  EOBServiceCodePaymentInfoViewDTO,
  EOBSummaryDTO,
} from 'dtos';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import Button from 'pages/Dashboard/components/Button';
import {
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import InsuranceChip from 'pages/Dashboard/components/InsuranceChips';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import Table from 'core/components/VirtualTable';
import {
  formatMoney,
  notEmptyString,
} from '@populateui/utils';
import {
  formatDateTime,
} from 'utils/date';
import ToolTip from 'core/components/Tooltip';

type Props = {
  eobSummaries: EOBSummaryDTO[];
  patient: PatientCompact;
  referenceDate?: string;
};

type EOBSummaryProps = {
  eob: EOBSummaryDTO;
  patient: PatientCompact;
  referenceDate?: string;
};

type AdjustmentsListProps = {
  serviceCodePayments: EOBServiceCodePaymentInfoViewDTO[];
};

type Adjustment = ClaimAdjustmentViewDTO & {
  billingCode?: string | null;
};

const serviceCodeAdjustmentColumns: ColumnDef<Adjustment>[] = [{
  id: 'billingCode',
  accessorKey: 'billingCode',
  header: 'Billing Code',
  meta: { autoSized: true },
}, {
  id: 'adjustmentCode',
  header: 'Adjustment Code',
  meta: { autoSized: true },
  cell: ({ row: { original: {
    groupCode,
    reasonCode,
    groupCodeDescription,
    reasonCodeDescription,
  } } }) => (
    <ToolTip title={(
      <div className="flex flex-col gap-1 p-1">
        <div className="flex gap-1">
          <span className="font-semibold w-6">
            {groupCode}
            :
          </span>
          <span>{groupCodeDescription}</span>
        </div>
        <div className="flex gap-1">
          <span className="font-semibold w-6">
            {reasonCode}
            :
          </span>
          <span className="max-w-[372px]">{reasonCodeDescription}</span>
        </div>
      </div>
      )}
    >
      <div className="flex items-center gap-1">{compact([groupCode, reasonCode]).join('-')}</div>
    </ToolTip>
  ),
}, {
  id: 'amount',
  accessorKey: 'amount',
  header: 'Amount',
  size: 120,
  cell: ({ row }) => formatMoney(row.original?.amount ?? 0),
}, {
  id: 'quantity',
  accessorKey: 'quantity',
  header: 'Quantity',
  className: '!block',
  size: 80,
  cell: ({ row: { original } }) => (isNil(original.quantity) ? '—' : original.quantity),
}];

function AdjustmentsList({ serviceCodePayments }: AdjustmentsListProps) {
  const adjustments = serviceCodePayments.map(
    (serviceInfo) => serviceInfo.adjustments?.map((adjustment) => ({
      ...adjustment,
      billingCode: serviceInfo.visitNoteBillingCode?.code ?? 'Not set',
    })),
  ).flat() as Adjustment[];

  return (
    <div className="flex flex-col gap-2 eob-adjustments-table">
      <Table<Adjustment>
        columns={serviceCodeAdjustmentColumns}
        classes={{
          scrollContainer: '!pb-0',
          row: '!items-start py-1',
          header: '!bg-slate-50 !py-1 !border-0 !text-gray-600 !font-medium',
          outerRow: '!border-0',
          scrollbarGutter: 'initial',
        }}
        data={adjustments ?? []}
        emptySlot={<div className="text-center items-center text-gray-500 w-full pl-2 pt-2">No adjustments posted on this EOB</div>}
      />
    </div>
  );
}

function EOBSummary({ eob, patient, referenceDate }: EOBSummaryProps) {
  const onOpenEOB = () => {
    paymentsEventChannel.emit('onEOBDialogOpen', {
      eobNumber: eob.eobNumber ?? '',
      defaultPatient: patient,
      defaultStartDate: referenceDate,
    });
  };

  return (
    <div className="bg-white shadow-sm flex flex-col  m-2 border rounded-lg border-gray-200">
      <div className="flex items-start gap-2 justify-between flex-1 p-2 bg-slate-100">
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="flex items-center gap-1 text-xs font-semibold">
            <div className="font-bold text-base whitespace-nowrap">{eob.eobNumber ?? '--'}</div>
            <div className="truncate flex-1">
              <InsuranceChip
                insuranceName={eob.insuranceName ?? 'Not set'}
                className="!bg-slate-100 !text-slate-700"
              />
            </div>
          </div>
          {notEmptyString(eob.transactionHandlingCode) && (
            <div className="text-xs text-gray-500">
              Transaction Code:
              {' '}
              <span className="font-semibold">{eob.transactionHandlingCode ?? '—'}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            variant="text"
            color="primary"
            className="!text-blue-500 font-semibold"
            onClick={(event) => {
              event.stopPropagation();
              onOpenEOB();
            }}
          >
            Open
          </Button>
          <div className="text-xs text-gray-600 flex items-center gap-1">
            <span>Created:</span>
            <span className="font-semibold">{formatDateTime(eob.createdOn ?? '')}</span>
          </div>
        </div>
      </div>
      <div>
        <AdjustmentsList serviceCodePayments={eob.serviceCodePaymentInfos ?? []} />
      </div>
    </div>
  );
}

export default function EOBSummaries({
  eobSummaries = [],
  patient,
  referenceDate,
}: Props) {
  return (
    <div className="text-xs">
      {eobSummaries.map((eob) => (
        <EOBSummary
          key={eob.eobNumber}
          eob={eob}
          patient={patient}
          referenceDate={referenceDate}
        />
      ))}
    </div>
  );
}
